import { Component, computed, forwardRef, inject, Injector, input, model, OnInit, viewChild } from '@angular/core';
import { AbstractControl, ControlValueAccessor, NG_VALUE_ACCESSOR, NgControl } from '@angular/forms';
import { DxTagBoxComponent, DxTagBoxModule, DxTextBoxModule } from 'devextreme-angular';
import { ValueChangedEvent } from 'devextreme/ui/tag_box';
import { OnboardingFile } from './models/onboarding-file';
import { DxButtonTypes } from 'devextreme-angular/ui/button';

@Component({
  selector: 'app-onboarding-file-selector',
  standalone: true,
  imports: [DxTextBoxModule, DxTagBoxModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => OnboardingFileSelectorComponent),
      multi: true
    }
  ],
  host: {
    class: 'link-component'
  },
  templateUrl: './onboarding-file-selector.component.html',
  styleUrl: './onboarding-file-selector.component.scss'
})
export class OnboardingFileSelectorComponent implements OnInit, ControlValueAccessor{
  private dx = viewChild.required<DxTagBoxComponent>('dx');
  private injector = inject(Injector);
  private ngControl: NgControl = null;
  fileInputId = `uploader-${crypto.randomUUID()}`;
  value = model<OnboardingFile>(null);
  readonly = input<boolean>(false);
  disabled = model(false);

  ngOnInit(): void {
    this.ngControl = this.injector.get(NgControl);
  }

  get isValid(): boolean {
    return !this.control || (this.control.invalid !== true || (this.control.pristine && this.control.untouched));
  }

  get control(): AbstractControl {
    return this.ngControl?.control;
  }

  dataSource = computed(() => this.computedValue() ? this.computedValue() : []);
  computedValue = computed(() => this.value() ? [this.value()] : []);

  selectFileOptions: DxButtonTypes.Properties = {
    text: 'Select File',
    icon: 'upload',
    stylingMode: 'text',
    type: 'default',
    onClick: () => void this.openFileSelect(),
  };

  openFileSelect() {
    const fileInput = document.getElementById(this.fileInputId);
    fileInput?.click();
  }

  onFileChosen(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = typeof reader.result === 'string' ? reader.result : '';
      const [meta, b64] = dataUrl.split(',');
      const mime = meta?.match(/data:(.*);base64/)?.[1] ?? 'application/octet-stream';

      const onboardingFile: OnboardingFile = {
        FileName: file.name,
        Data: b64 ?? null,
        Mime: mime,
      };

      this.onChange(onboardingFile);
      this.value.set(onboardingFile);
    };
    reader.readAsDataURL(file);
    input.value = '';
  }

  onValueChanged(e: ValueChangedEvent): void {
    const value = e.value as OnboardingFile[];
    const v = value[0] || null;

    if (v !== this.value()) {
      this.onChange(v);
      this.value.set(v);
    }
  }

  /*
   * ControlValueAccessor interface methods
   */

  writeValue(obj: OnboardingFile): void {
    this.value.set(obj);
  }

  onChange: (value: unknown) => void = () => { };
  registerOnChange(fn: (value: unknown) => void): void {
    this.onChange = fn;
  }

  onTouched: () => void = () => { };
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.dx().disabled = isDisabled;
  }
}
